import { z } from 'zod';
import { SUPPLY_TYPES } from './catalog.js';
// Company suppliers and defect repairs (DR-196). A defect is a finding of a closed handover (the
// authoritative record, never changed here); its repair has a status, a responsible contractor,
// an optional due date, an append-only history and private repair photos.
export const REPAIR_STATUSES = ['open', 'in_progress', 'fixed', 'verified'];
export const REPAIR_STATUS_LABELS = {
    open: 'פתוח',
    in_progress: 'בטיפול',
    fixed: 'תוקן, ממתין לבדיקה',
    verified: 'אושר ונסגר',
};
export const REPAIR_ACTIONS = [
    'released',
    'withdrawn',
    'started',
    'reported',
    'approved',
    'rejected',
    'assigned',
];
export const REPAIR_ACTION_LABELS = {
    released: 'שוחרר לטיפול הקבלן',
    withdrawn: 'השחרור לקבלן בוטל',
    started: 'הטיפול התחיל',
    reported: 'דווח שהתיקון בוצע',
    approved: 'התיקון אושר והליקוי נסגר',
    rejected: 'התיקון נדחה והוחזר לטיפול',
    assigned: 'שיוך לקבלן או מועד יעד',
};
export const DEFECT_LIMITS = { note: 1000, reportPhotos: 10, page: 200 };
const uuid = z.uuid();
const dateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const optionalNote = z
    .string()
    .trim()
    .max(DEFECT_LIMITS.note)
    .nullish()
    .transform((v) => (v === undefined || v === null || v === '' ? null : v));
/** A photo of the defect (from the handover) or of its repair; served by an authorized URL. */
export const defectPhotoSchema = z
    .object({ id: uuid, url: z.string(), kind: z.enum(['finding', 'repair']) })
    .strict();
/** One defect in a list: where, what, who and its state. No resident data, ever. */
export const defectSummarySchema = z
    .object({
    id: uuid,
    projectId: uuid,
    projectName: z.string(),
    building: z.string(),
    apartment: z.string(),
    area: z.string(),
    component: z.string(),
    workCategory: z.string().nullable(),
    description: z.string(),
    contractor: z.object({ id: uuid, name: z.string() }).nullable(),
    status: z.enum(REPAIR_STATUSES),
    /** Released to the contractor by Sivan staff (until then, the contractor sees nothing). */
    released: z.boolean(),
    /** The handover the finding belongs to: still a draft, or closed. */
    handoverStatus: z.enum(['draft', 'completed', 'refused']),
    dueOn: dateOnly.nullable(),
    overdue: z.boolean(),
    inspectedOn: dateOnly,
    protocolNumber: z.string(),
    handoverType: z.enum(['initial', 'final']),
    photo: defectPhotoSchema.nullable(),
    rowVersion: z.number().int(),
    updatedAt: z.string(),
})
    .strict();
export const defectCountsSchema = z
    .object({
    /** Saved findings not released to a contractor yet. */
    unreleased: z.number().int(),
    open: z.number().int(),
    inProgress: z.number().int(),
    fixed: z.number().int(),
    verified: z.number().int(),
    overdue: z.number().int(),
})
    .strict();
const option = z.object({ id: z.string(), name: z.string() }).strict();
/** `GET /projects/:projectId/defects`: the project's defects (staff). */
export const defectListResponseSchema = z
    .object({
    counts: defectCountsSchema,
    items: z.array(defectSummarySchema),
    /** The values the filters offer, from the project's defects. */
    filters: z
        .object({
        buildings: z.array(z.string()),
        areas: z.array(z.string()),
        workCategories: z.array(z.string()),
        contractors: z.array(option),
    })
        .strict(),
    /** Contractors of the project a defect can be assigned to. */
    assignable: z.array(option),
    permissions: z.object({ manage: z.boolean() }).strict(),
})
    .strict();
export const defectListQuerySchema = z
    .object({
    building: z.string().max(80).optional(),
    apartment: z.string().max(40).optional(),
    area: z.string().max(80).optional(),
    workCategory: z.string().max(80).optional(),
    contractorId: uuid.optional(),
    status: z.enum([...REPAIR_STATUSES, 'overdue', 'unreleased']).optional(),
    from: dateOnly.optional(),
    to: dateOnly.optional(),
    q: z.string().trim().max(80).optional(),
})
    .strict();
export const repairEventSchema = z
    .object({
    id: uuid,
    action: z.enum(REPAIR_ACTIONS),
    fromStatus: z.enum(REPAIR_STATUSES),
    toStatus: z.enum(REPAIR_STATUSES),
    contractor: z.string().nullable(),
    dueOn: dateOnly.nullable(),
    note: z.string().nullable(),
    actor: z.string(),
    /** The supplier the actor works for (null: a Sivan employee). */
    actorSupplier: z.string().nullable(),
    occurredAt: z.string(),
    photos: z.array(defectPhotoSchema),
})
    .strict();
/** One defect with its photos and repair history. */
export const defectDetailSchema = z
    .object({
    defect: defectSummarySchema,
    photos: z.array(defectPhotoSchema),
    history: z.array(repairEventSchema),
    /** Photos uploaded for a repair report that is not sent yet (the supplier's own). */
    pendingPhotos: z.array(defectPhotoSchema),
    actions: z
        .object({
        release: z.boolean(),
        withdraw: z.boolean(),
        start: z.boolean(),
        report: z.boolean(),
        approve: z.boolean(),
        reject: z.boolean(),
        assign: z.boolean(),
    })
        .strict(),
})
    .strict();
export const repairVersionSchema = z.object({ rowVersion: z.number().int().min(0) }).strict();
export const repairReportSchema = z
    .object({
    rowVersion: z.number().int().min(0),
    photoIds: z.array(uuid).min(1).max(DEFECT_LIMITS.reportPhotos),
    note: optionalNote,
})
    .strict();
export const repairRejectSchema = z
    .object({
    rowVersion: z.number().int().min(0),
    reason: z.string().trim().min(3).max(DEFECT_LIMITS.note),
})
    .strict();
export const repairAssignSchema = z
    .object({
    rowVersion: z.number().int().min(0),
    contractorId: uuid,
    dueOn: dateOnly.nullable(),
    note: optionalNote,
})
    .strict();
/** Releases a saved finding to a contractor (and its due date, set by hand or not at all). */
export const repairReleaseSchema = repairAssignSchema;
/** Withdraws a release (the contractor no longer sees it); the reason is kept in the history. */
export const repairWithdrawSchema = z
    .object({
    rowVersion: z.number().int().min(0),
    reason: z.string().trim().min(3).max(DEFECT_LIMITS.note),
})
    .strict();
/** `GET /portal/defects`: a supplier's own defects, across the projects it works in. */
export const portalResponseSchema = z
    .object({
    supplier: z.object({ id: uuid, name: z.string() }).strict(),
    counts: z
        .object({ todo: z.number().int(), waiting: z.number().int(), done: z.number().int() })
        .strict(),
    items: z.array(defectSummarySchema),
})
    .strict();
// ---- The company catalog ----------------------------------------------------------------------
export const COMPANY_SUPPLIER_KINDS = ['contractor', 'supplier'];
export const COMPANY_SUPPLIER_KIND_LABELS = {
    contractor: 'קבלן מבצע',
    supplier: 'ספק חומרים',
};
const optionalText = (max) => z
    .string()
    .trim()
    .max(max)
    .nullable()
    .transform((v) => (v === '' ? null : v));
export const companySupplierInputSchema = z
    .object({
    kind: z.enum(COMPANY_SUPPLIER_KINDS),
    name: z.string().trim().min(1).max(80),
    supplyType: z.enum(SUPPLY_TYPES).nullable(),
    contactName: optionalText(80),
    contactPhone: optionalText(25),
    contactEmail: optionalText(254),
    tradeIds: z.array(uuid).max(50),
    active: z.boolean(),
})
    .strict()
    .superRefine((v, ctx) => {
    if ((v.kind === 'supplier') !== (v.supplyType !== null))
        ctx.addIssue({ code: 'custom', path: ['supplyType'], message: 'required' });
});
export const companySupplierSchema = z
    .object({
    id: uuid,
    kind: z.enum(COMPANY_SUPPLIER_KINDS),
    name: z.string(),
    supplyType: z.enum(SUPPLY_TYPES).nullable(),
    contactName: z.string().nullable(),
    contactPhone: z.string().nullable(),
    contactEmail: z.string().nullable(),
    active: z.boolean(),
    tradeIds: z.array(uuid),
    projects: z.array(z.object({ id: uuid, name: z.string() }).strict()),
    people: z.array(z
        .object({
        employeeId: uuid,
        name: z.string(),
        email: z.string().nullable(),
        account: z.enum(['none', 'pending', 'active', 'deactivated']),
        projects: z.array(uuid),
    })
        .strict()),
    rowVersion: z.number().int(),
})
    .strict();
export const companyTradeSchema = z
    .object({ id: uuid, name: z.string(), active: z.boolean(), rowVersion: z.number().int() })
    .strict();
export const companyCatalogResponseSchema = z
    .object({
    suppliers: z.array(companySupplierSchema),
    trades: z.array(companyTradeSchema),
    projects: z.array(z.object({ id: uuid, name: z.string() }).strict()),
})
    .strict();
export const companyTradeInputSchema = z
    .object({ name: z.string().trim().min(1).max(80), active: z.boolean() })
    .strict();
/** Adds a company contractor to a project for some trades (creating the project entries). */
export const projectSupplierAssignSchema = z
    .object({ supplierId: uuid, tradeIds: z.array(uuid).max(50) })
    .strict();
/** A supplier's person: a directory entry linked to the supplier (invited like an employee). */
export const supplierPersonInputSchema = z
    .object({
    name: z.string().trim().min(2).max(120),
    email: z
        .string()
        .trim()
        .toLowerCase()
        .max(254)
        .regex(/^[^@\s]+@[^@\s]+\.[^@\s]+$/),
})
    .strict();
/** The projects a supplier's person may use the portal in. */
export const supplierPersonProjectsSchema = z
    .object({ projectIds: z.array(uuid).max(200) })
    .strict();
//# sourceMappingURL=defects.js.map